import React, { FC, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import {
  AppCardWrapper,
  AppCardFooter,
  FooterContentWrapper,
  TaskTextWrapper,
  MessageIcon,
  TaskInstruction,
  TaskName,
  CardHeader,
  BadgesContainer,
} from 'components/AppKanbanBoard/AppCard/AppCard.style'
import { AppBadge } from 'components/Common/AppBadge/AppBadge.component'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { getColorForGoalCategory, getColorForGoalPriority } from 'appUtils'
import appMessage from 'appAssets/appImages/appMessage.png'
import { categoryType, taskPriorityStatus } from 'appConfig/data'
import UnarchiveIcon from 'assets/svgs/unarchive.svg'
import { AppBinIcon } from 'components/Common/AppSvgIcons/AppBinIcon'
import {
  UpdateUserTaskInput,
  UserTask,
  UserTaskItemStatus,
  UserTaskSuggestedItemStatus,
  useUpdateUserTaskMutation,
  useGetTaskCommentsQuery,
} from '__generated__/api-types-and-hooks'
import { AppPaperClipIcon } from 'components/Common/AppSvgIcons/AppPaperClipIcon'
import { AppSuggestedByBox } from 'components/Common/AppSuggestedByBox/AppSuggestedByBox.component'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import CircularLoading from 'components/Common/CircularLoading'
import { IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import { AvatarColumn } from '../AppStatusBar/AppTaskStatusBar.style'
import { AppNameInitials } from 'components/Common/AppNameInitials/AppNameInitials.component'

interface IAppTaskCard {
  task: UserTask
  userId: string
  userAvatar: string
  userFirstName: string
  userLastName: string
  onTrashClick?: () => void
  refetch?: () => void
  isArchiveView?: boolean
  onUnarchive?: (taskId: string) => void
  unarchivingTaskId?: string | null
  advisorsData?: Array<{ label: any; value: any }>
  assigneeOptions?: IAssigneeOptions[]
}

export const AppTaskCard: FC<IAppTaskCard> = ({
  task,
  userId,
  userAvatar,
  userFirstName,
  userLastName,
  onTrashClick,
  refetch,
  isArchiveView,
  onUnarchive,
  unarchivingTaskId,
  advisorsData,
  assigneeOptions,
}) => {
  const queryClient = useQueryClient()
  const currentUserId = useSelector((state: RootState) => state.user.user.id)
  const [loadingSuggestedTask, setLoadingSuggestedTask] = useState<boolean>(false)

  // Fallback: Fetch comment count if comments are not available from the task
  const { data: commentsData } = useGetTaskCommentsQuery(
    { input: { taskId: task.taskId ?? '' } },
    {
      enabled: !!task.taskId && (task.comments === null || task.comments === undefined),
      refetchOnWindowFocus: false,
    }
  )

  // Use comments from task if available, otherwise use fetched comments
  const comments = task.comments ?? commentsData?.getTaskComments ?? null
  const commentCount = comments && Array.isArray(comments) ? comments.length : 0
  const isSuggestedTask = useMemo(() => {
    // Check if task is suggested by Coach Logic or another user
    const isCoachLogicSuggested = task?.createdBy === 'Coach Logic' && task?.source === 'INSIGHT'
    const isUserSuggested = task?.createdBy !== 'GoTackle' && task?.createdById !== task?.assigneeId

    return isUserSuggested || isCoachLogicSuggested
  }, [task])
  const user = useSelector((state: RootState) => state.user.user)
  const isAdvisor = user?.roles?.includes('BSO_ADVISOR')

  //const shownUserName = isAdvisor ? advisor?.label : `${userFirstName} ${userLastName}`
  const shownUserName = `${userFirstName} ${userLastName}`
  const assignee =
    assigneeOptions?.find((assignee) => assignee.value === task?.assigneeId)?.label || shownUserName
  const assigneeInitials = assignee
    ?.split(' ')
    .map((name) => name[0])
    .join('')
  const {
    mutate: updateUserTask,
    isLoading: isTaskUpdating,
    isSuccess: isTaskUpdateSuccess,
  } = useUpdateUserTaskMutation()

  const handleUpdateSuggestedTask = (suggestedStatus: UserTaskSuggestedItemStatus) => {
    setLoadingSuggestedTask(true)
    const input: UpdateUserTaskInput = {
      taskId: task.taskId!,
      tenantId: task.tenantId!,
      // userId: task.createdById ?? task.userId,
      userId: task.userId,
      suggestedStatus,
      status:
        suggestedStatus === UserTaskSuggestedItemStatus.Rejected
          ? UserTaskItemStatus.Archived
          : UserTaskItemStatus.Todo,
    }
    updateUserTask(
      { input },
      {
        onSuccess: () => {
          // Invalidate all relevant queries to ensure data is refetched
          queryClient.invalidateQueries('getUserTasks')
          queryClient.invalidateQueries('getAdvisorAssignedTasks')
          queryClient.invalidateQueries('getBusinessGoal')
          queryClient.invalidateQueries('getUserTask')

          // Also call refetch for immediate update
          refetch && refetch()
        },
        onSettled: () => {
          setLoadingSuggestedTask(false)
        },
      }
    )
  }

  const handleUnarchive = (taskId: string) => {
    onUnarchive && onUnarchive(taskId)
  }

  // Check if this specific task is being unarchived
  const isThisTaskUnarchiving = unarchivingTaskId === task.taskId

  useEffect(() => {
    if (isTaskUpdateSuccess) {
      refetch && refetch()
    }
  }, [isTaskUpdateSuccess, refetch])

  const path = window.location.pathname
  const isOnAllTasksOrDashboard = path.includes('all-plays') || path.includes('/dashboard')

  return (
    <AppCardWrapper>
      {isAdvisor && isOnAllTasksOrDashboard && task.businessName && (
        <div className="flex items-center gap-2">
          <div className="w-6">
            {task.businessLogo || task.businessAvatar ? (
              <AvatarColumn>
                <AppAvatar
                  size="xs"
                  type={'image'}
                  shape={'Rounded'}
                  src={task.businessLogo || task.businessAvatar || ''}
                />
              </AvatarColumn>
            ) : (
              <AppNameInitials
                name={{
                  firstName: task.businessName?.split(' ')[0] || '',
                  lastName: task.businessName?.split(' ')[1] || '',
                }}
                className="bg-tertiary w-6 h-6"
              />
            )}
          </div>
          <div className="text-sm font-inter font-medium text-app-grey-100">
            {task.businessName}
          </div>
        </div>
      )}
      {isSuggestedTask && (
        <div className="w-full">
          <AppSuggestedByBox
            onAccept={() => handleUpdateSuggestedTask(UserTaskSuggestedItemStatus.Accepted)}
            onReject={() => handleUpdateSuggestedTask(UserTaskSuggestedItemStatus.Rejected)}
            status={task.suggestedStatus ?? UserTaskSuggestedItemStatus.Pending}
            name={{
              firstName: task.createdBy?.split(' ')[0] ?? '',
              lastName: task.createdBy?.split(' ')[1] ?? '',
            }}
            isDisabled={task.assigneeId !== currentUserId || isTaskUpdating}
            isLoading={loadingSuggestedTask}
          />
        </div>
      )}
      <CardHeader>
        <TaskTextWrapper>
          <TaskName>{task.name}</TaskName>
          <TaskInstruction>{task.instructionForTask}</TaskInstruction>
        </TaskTextWrapper>
        <div className={`flex items-center gap-1 justify-end flex-shrink-0`}>
          {!isArchiveView && (
            <div className="flex items-start gap-1">
              {commentCount > 0 && (
                <span
                  className="text-app-grey-70 text-sm font-medium -mt-0.5"
                  title={`${commentCount} comment${commentCount !== 1 ? 's' : ''}`}
                >
                  {commentCount}
                </span>
              )}
              <MessageIcon src={appMessage} alt="messagebox" />
            </div>
          )}
          {task.files && task.files.length > 0 && !isArchiveView && (
            <div>
              <AppPaperClipIcon color="#7C7C7C" />
            </div>
          )}
          {onTrashClick && !isArchiveView && (
            <div
              onClick={(event) => {
                event.stopPropagation()
                event.preventDefault()
                onTrashClick()
              }}
              onMouseDown={(event) => {
                event.stopPropagation()
              }}
              className="cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-center p-1"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onTrashClick()
                }
              }}
              aria-label="Delete play"
              style={{ pointerEvents: 'auto', zIndex: 10 }}
            >
              <AppBinIcon />
            </div>
          )}
          {isArchiveView && onUnarchive && (
            <div
              onClick={(event) => {
                event.stopPropagation()
                handleUnarchive(task.taskId!)
              }}
              className="cursor-pointer w-6 h-6 flex items-center justify-center"
            >
              {isThisTaskUnarchiving ? (
                <CircularLoading className="w-5 h-5 app-bg-primary" />
              ) : (
                <img src={UnarchiveIcon} alt="unarchive" />
              )}
            </div>
          )}
        </div>
      </CardHeader>

      <AppCardFooter>
        <FooterContentWrapper>
          <BadgesContainer>
            {task.category && (
              <AppBadge
                size="xs"
                type="Filled"
                color={getColorForGoalCategory(task.category)}
                label={categoryType[task.category]}
              />
            )}
            {task.taskPriority && (
              <AppBadge
                size="xs"
                type="Filled"
                color={getColorForGoalPriority(task.taskPriority)}
                label={taskPriorityStatus[task.taskPriority]}
              />
            )}
            {task.isOverDue && <AppBadge size="xs" type="Filled" color={'Red'} label={'Overdue'} />}
          </BadgesContainer>
          <AppAvatar size="sm" type="mixed" shape="Rounded" text={assigneeInitials} />
        </FooterContentWrapper>
      </AppCardFooter>
    </AppCardWrapper>
  )
}
